import { useRef, useState, useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import axios from "../../axios"
import { ReactComponent as ArrowRight } from "../../assets/images/arrow-right.svg"
import { ReactComponent as ChevronSort } from "../../assets/images/chevron-sort.svg"
import { getBreadCrumbs } from "../../store/bread-crumbs/bread-crumbs.selectors"
import BlurText from "./BlurText"
import BreadcrumbDropdown from "./BreadcrumbDropdown"
import "../../assets/css/breadcrumb-nav.scss"

const PASSTHROUGH_PARAMS = ["week"]

const buildMergedHref = (url, currentSearchParams) => {
  const [path, existingSearch = ""] = url.split("?")
  const urlSearchParams = new URLSearchParams(existingSearch)

  PASSTHROUGH_PARAMS.forEach((key) => {
    const values = currentSearchParams.getAll(key)

    if (values.length) {
      urlSearchParams.delete(key)

      values.forEach((value) => {
        if (value != null && value !== "") urlSearchParams.append(key, value)
      })
    }
  })

  const query = urlSearchParams.toString()

  if (query) return `${path}?${query}`

  return path
}

const BreadcrumbNav = () => {
  const nav = useNavigate()
  const { search } = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])
  const breadCrumbs = useSelector(getBreadCrumbs)

  const [openDropdownIndex, setOpenDropdownIndex] = useState(null)
  const [isResolving, setIsResolving] = useState(false)
  const anchorRefs = useRef({})

  const getAnchorRef = useCallback((index) => {
    if (!anchorRefs.current[index]) {
      anchorRefs.current[index] = { current: null }
    }
    return anchorRefs.current[index]
  }, [])

  const handleNavigate = useCallback(
    (url) => {
      if (url) nav(buildMergedHref(url, searchParams))
    },
    [nav, searchParams]
  )

  const handleLabelClick = useCallback(
    (el, index) => {
      if (isResolving) return
      if (el.url && index < breadCrumbs.length - 1) {
        handleNavigate(el.url)
      }
    },
    [breadCrumbs.length, handleNavigate, isResolving]
  )

  const handleChevronClick = useCallback(
    (e, index) => {
      e.stopPropagation()
      if (isResolving) return
      setOpenDropdownIndex((prev) => (prev === index ? null : index))
    },
    [isResolving]
  )

  const handleDropdownSelect = useCallback(
    async (item) => {
      setOpenDropdownIndex(null)

      if (!item.stationId || item.isActive) {
        handleNavigate(item.url)
        return
      }

      setIsResolving(true)
      try {
        const response = await axios.get(`/payroll/${item.id}`)
        const match = response.data.statements.find(
          (s) => s.station_id === item.stationId
        )
        if (match) {
          handleNavigate(`/run-payroll-preview/${item.id}/${match.id}`)
        } else {
          handleNavigate(item.url)
        }
      } catch {
        handleNavigate(item.url)
      } finally {
        setIsResolving(false)
      }
    },
    [handleNavigate]
  )

  if (breadCrumbs.length === 0) return null

  return (
    <div className="breadcrumb-nav">
      {breadCrumbs.map((el, index) => {
        const isLast = index === breadCrumbs.length - 1
        const hasItems = el.items?.length > 1
        const anchorRef = getAnchorRef(index)

        return (
          <div key={`${el.name}-${index}`} className="breadcrumb-segment">
            <div className="breadcrumb-label-wrapper" style={{ position: "relative" }}>
              <div
                ref={(node) => {
                  anchorRef.current = node
                }}
                className={`breadcrumb-label${isLast && !hasItems ? " active" : ""}${hasItems ? " has-dropdown" : ""}`}
                onClick={() => handleLabelClick(el, index)}
              >
                {index === 2 ? <BlurText>{el.name}</BlurText> : el.name}
                {hasItems && (
                  <ChevronSort
                    className="breadcrumb-chevron"
                    onClick={(e) => handleChevronClick(e, index)}
                  />
                )}
              </div>
              <BreadcrumbDropdown
                items={el.items || []}
                activeItemId={el.activeItemId}
                isOpen={openDropdownIndex === index}
                onClose={() => setOpenDropdownIndex(null)}
                onSelect={handleDropdownSelect}
                anchorRef={anchorRef}
              />
            </div>

            {!isLast && <ArrowRight className="header-sec-bread-crumb-arrow" />}
          </div>
        )
      })}
    </div>
  )
}

export default BreadcrumbNav
